import React, { useMemo, useState } from "react";

import moment from "moment";

import { useGetDataSubmissionDates, useGetFarmReportingYear, useGetFarms, useTranslation } from "utils";

import { Button, Flex, Icon, Progress, Typography } from "components";

import { DataSubmissionBarConfirmationModal } from "pages/app/all-field-view/components/DataSubmissionBarConfirmationModal";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";

type DataSubmissionBarProps = {
  enrolledFieldsCount: number;
  fieldsWithMissingTasksCount: number;
  openTasksCount: number;
  openMissingDocumentsCount: number;
  totalMissingDocumentsCount: number;
  tasksTriggerButtonCount: number;
  onOpenTasksClick?: () => void;
  onSubmitClick?: () => Promise<void>;
};

const PROGRESS_CONFIG = {
  SIZE: 72,
  STROKE_WIDTH: 8,
  PROGRESS_CIRCLE_STROKE_COLOR: "#065c45", // variables.$green-400
} as const;

enum DATA_SUBMISSION_BAR_STATUS {
  NOT_STARTED = "notStarted",
  IN_PROGRESS = "inProgress",
  SUBMISSION_OPEN = "submissionOpen",
  SUBMISSION_OVERDUE = "submissionOverdue",
  SUBMISSION_READY = "submissionReady",
  SUBMISSION_REOPENED = "submissionReopened",
  SUBMISSION_REOPENED_OVERDUE = "submissionReopenedOverdue",
  SUBMISSION_REOPENED_READY = "submissionReopenedReady",
  SUBMISSION_COMPLETED = "submissionCompleted",
}

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

const calculateProgressPercent = (
  enrolledFieldsCount: number,
  fieldsWithMissingTasksCount: number,
  openMissingDocumentsCount: number,
  totalMissingDocumentsCount: number
): number => {
  const safeEnrolledFieldsCount = Math.max(1, enrolledFieldsCount);
  const completedFields = Math.max(0, enrolledFieldsCount - fieldsWithMissingTasksCount);

  const openTasksCompletionRatio = clamp01(completedFields / safeEnrolledFieldsCount);

  // If there are no missing document requirements at all, missing data accounts for 100% of progress.
  // If missing documents exist, missing data accounts for 90% and missing documents account for 10%.
  const hasMissingDocuments = totalMissingDocumentsCount > 0;
  const openTasksWeight = hasMissingDocuments ? 90 : 100;
  const documentsWeight = hasMissingDocuments ? 10 : 0;

  const safeTotalMissingDocumentsCount = hasMissingDocuments ? Math.max(1, totalMissingDocumentsCount) : 0;
  const completedDocuments = hasMissingDocuments ? Math.max(0, totalMissingDocumentsCount - openMissingDocumentsCount) : 0;
  const documentsCompletionRatio = hasMissingDocuments ? clamp01(completedDocuments / safeTotalMissingDocumentsCount) : 1;

  const progressFloat = openTasksCompletionRatio * openTasksWeight + documentsCompletionRatio * documentsWeight;
  const rounded = Math.round(progressFloat);

  // Never show 100% while there are still open tasks in any category (avoid rounding to 100 early).
  const hasBlockingTasks = fieldsWithMissingTasksCount > 0 || openMissingDocumentsCount > 0;

  return Math.max(0, Math.min(hasBlockingTasks ? 99 : 100, rounded));
};

export const DataSubmissionBar = ({
  enrolledFieldsCount,
  fieldsWithMissingTasksCount,
  openTasksCount,
  openMissingDocumentsCount,
  totalMissingDocumentsCount,
  tasksTriggerButtonCount,
  onOpenTasksClick,
  onSubmitClick,
}: DataSubmissionBarProps) => {
  const { t } = useTranslation("allFieldView");
  const { currentFarm } = useGetFarms();
  const {
    data: { activeFarmReportingYear },
  } = useGetFarmReportingYear(currentFarm?.id || null);
  const { data: dataSubmissionDates } = useGetDataSubmissionDates({ farmId: currentFarm?.id });

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isDataSubmitted, isDataSubmissionReopened } = useSubmissionStatus(activeFarmReportingYear);

  const isProgramEnabled = !!currentFarm?.carbonProgram;

  const today = moment();
  const dueDate = moment(dataSubmissionDates?.submissionDueDate);

  const isSubmissionPeriodOpen = dataSubmissionDates?.isSubmissionPeriodOpen;

  const isOverdue = useMemo(() => isSubmissionPeriodOpen && dueDate.isValid() && today.isSameOrAfter(dueDate, "day"), [isSubmissionPeriodOpen, today, dueDate]);
  const shouldShowBar = isProgramEnabled && enrolledFieldsCount > 0;

  const progressPercent = useMemo(
    () => calculateProgressPercent(enrolledFieldsCount, fieldsWithMissingTasksCount, openMissingDocumentsCount, totalMissingDocumentsCount),
    [enrolledFieldsCount, fieldsWithMissingTasksCount, openMissingDocumentsCount, totalMissingDocumentsCount]
  );

  const totalOpenTasks = openMissingDocumentsCount + openTasksCount;

  const barStatus = useMemo(() => {
    if (!isSubmissionPeriodOpen) {
      if (progressPercent === 0) {
        return DATA_SUBMISSION_BAR_STATUS.NOT_STARTED;
      }
      return DATA_SUBMISSION_BAR_STATUS.IN_PROGRESS;
    }

    if (isDataSubmitted && !isDataSubmissionReopened) {
      return DATA_SUBMISSION_BAR_STATUS.SUBMISSION_COMPLETED;
    }

    if (progressPercent === 100) {
      if (isDataSubmissionReopened) {
        return DATA_SUBMISSION_BAR_STATUS.SUBMISSION_REOPENED_READY;
      }
      return DATA_SUBMISSION_BAR_STATUS.SUBMISSION_READY;
    }

    if (isOverdue) {
      if (isDataSubmissionReopened) {
        return DATA_SUBMISSION_BAR_STATUS.SUBMISSION_REOPENED_OVERDUE;
      }
      return DATA_SUBMISSION_BAR_STATUS.SUBMISSION_OVERDUE;
    }

    if (isDataSubmissionReopened) {
      return DATA_SUBMISSION_BAR_STATUS.SUBMISSION_REOPENED;
    }

    return DATA_SUBMISSION_BAR_STATUS.SUBMISSION_OPEN;
  }, [isSubmissionPeriodOpen, progressPercent, isDataSubmitted, isDataSubmissionReopened, isOverdue]);

  const showRedText = isOverdue && barStatus !== DATA_SUBMISSION_BAR_STATUS.SUBMISSION_COMPLETED;

  if (!shouldShowBar || !activeFarmReportingYear) {
    return null;
  }

  return (
    <div className="data-submission-bar">
      <Flex vertical gap="1rem" className="submission-bar-content">
        <Flex align="center" justify="space-between" gap="2rem">
          <div className="progress-circle-container">
            <Progress
              type="circle"
              // we want to avoid showing a submission completion message but an incomplete percentage
              percent={isDataSubmitted && !isDataSubmissionReopened ? 100 : progressPercent}
              size={PROGRESS_CONFIG.SIZE}
              strokeWidth={PROGRESS_CONFIG.STROKE_WIDTH}
              strokeColor={PROGRESS_CONFIG.PROGRESS_CIRCLE_STROKE_COLOR}
            />
          </div>
          <Flex vertical style={{ flex: 1 }}>
            <Typography variant="paragraph-l" bold className="margin-0">
              {t(`submissionBar.status.${barStatus}.title`, { year: activeFarmReportingYear })}
            </Typography>
            <Typography variant="paragraph-m" color={showRedText ? "red-500" : undefined} className="margin-0">
              {t(`submissionBar.status.${barStatus}.description`, { count: totalOpenTasks, year: activeFarmReportingYear })}
            </Typography>
          </Flex>
          <Flex gap="1.5rem" align="center" className="data-submission-bar-actions-container">
            {isSubmissionPeriodOpen && barStatus !== DATA_SUBMISSION_BAR_STATUS.SUBMISSION_COMPLETED && (
              <Flex gap="0.5rem" align="center" className="data-submission-bar-time-left-container">
                <Icon iconName="tabler/calendar-time" className={`data-submission-bar-icon ${showRedText ? "red-text" : ""}`} />
                <Typography variant="paragraph-m" color={showRedText ? "red-500" : undefined} className="data-submission-bar-time-left-text">
                  {isOverdue ? t("submissionBar.overdue") : t("submissionBar.due", { dueDate: dueDate.format("MMM DD") })}
                </Typography>
              </Flex>
            )}
            <Flex gap="1rem" align="center">
              {!isDataSubmitted && (
                <Button type="primary" onClick={onOpenTasksClick} data-testid="open-tasks-cta">
                  {t("submissionBar.viewTasks")} ({tasksTriggerButtonCount})
                </Button>
              )}
              {isSubmissionPeriodOpen && barStatus !== DATA_SUBMISSION_BAR_STATUS.SUBMISSION_COMPLETED && (
                <Button type="primary" disabled={!!totalOpenTasks || isSubmitting} onClick={() => setShowConfirmationModal(true)} data-testid="submit-data-cta">
                  {t("submissionBar.submit")}
                </Button>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <DataSubmissionBarConfirmationModal
        showConfirmationModal={showConfirmationModal}
        setShowConfirmationModal={setShowConfirmationModal}
        onSubmitClick={onSubmitClick}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
      />
    </div>
  );
};
